import { useEffect, useState } from "react";
import { api, errorMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Panel } from "@/components/Panel";
import { Combobox, MultiCombobox } from "@/components/Combobox";
import { ModelosAgrupados } from "@/components/ModelosAgrupados";
import { useBusquedaLive } from "@/lib/busquedaHeader";
import { Plus, X, Pencil, ArrowUp, ArrowDown } from "lucide-react";

const ACCIONES = ["", "I", "C", "R", "A", "L"];
const ACCION_TITULO = { I: "Revisar", C: "Limpiar", R: "Cambiar", A: "Ajustar", L: "Lubricar" };
const UNIDADES = [
  { value: "km", label: "mil km" },
  { value: "anios", label: "años" },
  { value: "meses", label: "meses" },
];
const N_INTERVALOS = 7;

const soloDecimal = (s, entMax = 4) => {
  let v = s.replace(/[^\d.]/g, "");
  const i = v.indexOf(".");
  if (i !== -1) v = v.slice(0, i + 1) + v.slice(i + 1).replace(/\./g, "");
  const [ent, dec] = v.split(".");
  return dec !== undefined ? `${ent.slice(0, entMax)}.${dec.slice(0, 2)}` : ent.slice(0, entMax);
};

const filaVacia = () => ({
  key: Math.random().toString(36).slice(2), servicio_id: "",
  acciones_km: Array(N_INTERVALOS).fill(""), accion_anual: "",
  rp_valor: "", rp_unidad: "km",
});

const FORM_VACIO = {
  id: null, servicio_ids: [], nombre: "",
  tipos_periodo: { kilometraje: true, tiempo: false, horas: false },
};

const Campo = ({ label, obligatorio, error, testId, children }) => (
  <div className="space-y-1.5">
    <label className="text-sm font-medium text-slate-700">
      {label} {obligatorio && <span className="text-red-500">*</span>}
    </label>
    {children}
    {error && <p data-testid={`${testId}-error`} className="text-xs text-red-500">{error}</p>}
  </div>
);

const SelectAccion = ({ value, onChange, testId }) => (
  <Select value={value || "--"} onValueChange={(v) => onChange(v === "--" ? "" : v)}>
    <SelectTrigger data-testid={testId} className="h-8 w-16 justify-center px-1 text-xs font-semibold">
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      {ACCIONES.map((a) => (
        <SelectItem key={a || "--"} value={a || "--"}>
          {a ? `${a} — ${ACCION_TITULO[a]}` : "--"}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

export default function Cronogramas() {
  const [cronogramas, setCronogramas] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [serviciosTodos, setServiciosTodos] = useState([]);
  const [filtroModelo, setFiltroModelo] = useState("");
  const [q] = useBusquedaLive("Buscar en cronogramas…");
  const [form, setForm] = useState(FORM_VACIO);
  const [modelosSel, setModelosSel] = useState([]);
  const [maBusqueda, setMaBusqueda] = useState("");
  const [intervalos, setIntervalos] = useState(Array(N_INTERVALOS).fill(""));
  const [filas, setFilas] = useState([filaVacia()]);
  const [copiarId, setCopiarId] = useState("");
  const [errores, setErrores] = useState({});
  const [guardando, setGuardando] = useState(false);

  const cargarLista = async () => {
    const { data } = await api.get("/cronogramas");
    setCronogramas(data);
  };

  useEffect(() => {
    (async () => {
      const [m, s, t] = await Promise.all([api.get("/modelos"),
        api.get("/servicios", { params: { solo_cronograma: true } }), api.get("/servicios")]);
      setModelos(m.data); setServicios(s.data); setServiciosTodos(t.data);
      cargarLista();
    })();
  }, []);

  const set = (campo, v) => setForm((f) => ({ ...f, [campo]: v }));

  const aFilas = (detalle, intervalosKm) => detalle.map((f) => ({
    key: Math.random().toString(36).slice(2),
    servicio_id: f.servicio_id,
    acciones_km: [...(f.acciones_km || []).slice(0, N_INTERVALOS),
      ...Array(Math.max(0, N_INTERVALOS - (f.acciones_km || []).length)).fill("")],
    accion_anual: f.accion_anual || "",
    rp_valor: f.reemplazo_periodico
      ? String(f.reemplazo_periodico.unidad === "km" ? f.reemplazo_periodico.valor / 1000 : f.reemplazo_periodico.valor)
      : "",
    rp_unidad: f.reemplazo_periodico?.unidad || "km",
  }));

  const aIntervalos = (arr) => {
    const r = (arr || []).map((v) => String(v / 1000));
    return [...r, ...Array(Math.max(0, N_INTERVALOS - r.length)).fill("")];
  };

  const nuevo = () => {
    setForm(FORM_VACIO);
    setModelosSel([]);
    setMaBusqueda("");
    setIntervalos(Array(N_INTERVALOS).fill(""));
    setFilas([filaVacia()]);
    setCopiarId(""); setErrores({});
  };

  const editar = async (c) => {
    const { data } = await api.get(`/cronogramas/${c.id}`);
    setForm({ id: data.id, servicio_ids: data.servicio_ids || [], nombre: data.nombre,
      tipos_periodo: { kilometraje: true, tiempo: false, horas: false, ...data.tipos_periodo } });
    setModelosSel((data.modelos_aplicables || []).map((ma) => ma.modelo_id));
    setMaBusqueda("");
    setIntervalos(aIntervalos(data.intervalos_km));
    setFilas(data.detalle.length ? aFilas(data.detalle) : [filaVacia()]);
    setCopiarId(""); setErrores({});
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const copiarDesde = async (id) => {
    setCopiarId(id);
    if (!id) return;
    const { data } = await api.get(`/cronogramas/${id}`);
    setIntervalos(aIntervalos(data.intervalos_km));
    setFilas(data.detalle.length ? aFilas(data.detalle) : [filaVacia()]);
    toast.success(`Intervalos y filas copiados de "${data.nombre}". Ajuste lo necesario.`);
  };

  const eliminar = async (c) => {
    if (!window.confirm(`¿Eliminar el cronograma "${c.nombre}"?`)) return;
    try {
      await api.delete(`/cronogramas/${c.id}`);
      toast.success("Cronograma eliminado");
      if (form.id === c.id) nuevo();
      cargarLista();
    } catch (err) { toast.error(errorMsg(err)); }
  };

  const setFila = (key, campo, valor) =>
    setFilas((prev) => prev.map((f) => (f.key === key ? { ...f, [campo]: valor } : f)));

  const setAccionKm = (key, idx, valor) =>
    setFilas((prev) => prev.map((f) => (f.key === key
      ? { ...f, acciones_km: f.acciones_km.map((a, j) => (j === idx ? valor : a)) } : f)));

  const mover = (i, dir) => {
    const arr = [...filas];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setFilas(arr);
  };

  const guardar = async () => {
    const e = {};
    if (!form.nombre.trim()) e.nombre = "Ingrese el nombre del cronograma";
    setErrores(e);
    if (Object.keys(e).length) {
      toast.error(Object.values(e)[0]); // nunca queda mudo: siempre avisa
      return;
    }
    const idxValidos = intervalos.map((v, i) => [parseFloat(v), i])
      .filter(([v]) => v > 0).map(([, i]) => i);
    // Solo se envían las filas de detalle con servicio elegido; las vacías se descartan
    // (no bloquean el guardado y evitan el 422 "seleccione un servicio del catálogo").
    const filasValidas = filas.filter((f) => f.servicio_id);
    const payload = {
      servicio_ids: form.servicio_ids,
      nombre: form.nombre,
      // Cada modelo ya tiene su propio rango de años: aquí no se guardan años.
      modelos_aplicables: modelosSel.map((id) => ({ modelo_id: id, anio_inicial: null, anio_final: null })),
      tipos_periodo: form.tipos_periodo,
      intervalos_km: idxValidos.map((ix) => Math.round(parseFloat(intervalos[ix]) * 1000)),
      detalle: filasValidas.map((f) => ({
        servicio_id: f.servicio_id,
        acciones_km: idxValidos.map((ix) => f.acciones_km[ix]),
        accion_anual: f.accion_anual,
        reemplazo_periodico: f.rp_valor && parseFloat(f.rp_valor) > 0
          ? { valor: f.rp_unidad === "km" ? Math.round(parseFloat(f.rp_valor) * 1000) : parseFloat(f.rp_valor),
              unidad: f.rp_unidad }
          : null,
      })),
    };
    setGuardando(true);
    try {
      const { data } = form.id
        ? await api.put(`/cronogramas/${form.id}`, payload)
        : await api.post("/cronogramas", payload);
      set("id", data.id);
      toast.success(form.id ? "Cronograma actualizado de forma exitosa" : "Cronograma creado de forma exitosa");
      if (data.advertencia) toast.warning(data.advertencia, { duration: 8000 });
      cargarLista();
    } catch (err) { toast.error(errorMsg(err)); }
    finally { setGuardando(false); }
  };

  const qn = q.trim().toLowerCase();
  const listado = cronogramas.filter((c) =>
    (!filtroModelo || (c.modelos_aplicables || []).some((ma) => ma.modelo_id === filtroModelo)) &&
    (!qn || (c.nombre || "").toLowerCase().includes(qn)));
  const opcionesModelo = modelos.map((m) => ({ value: m.id, label: m.nombre_mostrar }));
  const nombreModelo = (id) => {
    const m = modelos.find((x) => x.id === id);
    return m ? (m.nombre_mostrar || `${m.marca} ${m.modelo}`) : "—";
  };
  const nombreServicioAsociado = (id) => {
    const s = serviciosTodos.find((x) => x.id === id);
    return s ? s.nombre : "—";
  };
  const opcionesServiciosAsociados = serviciosTodos.filter(
    (s) => s.abre_cronograma || (form.servicio_ids || []).includes(s.id));
  const maBusq = maBusqueda.trim().toLowerCase();
  const modelosFiltrados = maBusq
    ? modelos.filter((m) => (m.nombre_mostrar || `${m.marca} ${m.modelo}`).toLowerCase().includes(maBusq))
    : modelos;
  const etiquetaMa = (ma) => `${ma.modelo?.nombre_mostrar || "—"}${ma.anio_inicial
    ? ` ${ma.anio_inicial}${ma.anio_final ? `–${ma.anio_final}` : "+"}` : ""}`;
  const modelosTxt = (c) => {
    const mas = c.modelos_aplicables || [];
    if (!mas.length) return "—";
    const primeros = mas.slice(0, 2).map(etiquetaMa).join(" · ");
    return mas.length > 2 ? `${primeros} +${mas.length - 2}` : primeros;
  };
  const serviciosTxt = (c) => {
    const ss = c.servicios || [];
    if (!ss.length) return "—";
    const primeros = ss.slice(0, 2).map((s) => s.nombre).join(" · ");
    return ss.length > 2 ? `${primeros} +${ss.length - 2}` : primeros;
  };

  return (
    <div data-testid="cronogramas-page" className="space-y-4">
      <Panel titulo="Cronogramas de Mantenimiento" testId="cronogramas-lista-panel">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <div className="w-72">
            <Combobox testId="cronogramas-filtro-modelo" value={filtroModelo} onChange={setFiltroModelo}
              placeholder="Filtrar por modelo" options={opcionesModelo} />
          </div>
          <Button variant="outline" data-testid="cronograma-nuevo-btn"
            className="ml-auto border-teal-500 text-teal-600 hover:bg-teal-50 hover:text-teal-700" onClick={nuevo}>
            <Plus size={15} className="mr-1" /> Nuevo cronograma
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table data-testid="cronogramas-tabla" className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                <th className="py-2.5 pr-3 font-semibold">Nombre</th>
                <th className="py-2.5 pr-3 font-semibold">Servicios asociados</th>
                <th className="py-2.5 pr-3 font-semibold">Modelos (años)</th>
                <th className="py-2.5 pr-3 font-semibold">Intervalos (x1.000 km)</th>
                <th className="py-2.5 pr-3 font-semibold">Ítems</th>
                <th className="py-2.5 text-right font-semibold">Acción</th>
              </tr>
            </thead>
            <tbody>
              {listado.map((c) => (
                <tr key={c.id} data-testid={`cronograma-row-${c.id}`} className="border-b border-slate-100">
                  <td className="py-2.5 pr-3 font-medium text-slate-700">{c.nombre}</td>
                  <td className="py-2.5 pr-3 text-slate-600" data-testid={`cronograma-servicios-${c.id}`}>{serviciosTxt(c)}</td>
                  <td className="py-2.5 pr-3 text-slate-600" data-testid={`cronograma-modelos-${c.id}`}>{modelosTxt(c)}</td>
                  <td className="py-2.5 pr-3 text-slate-600">{(c.intervalos_km || []).map((v) => v / 1000).join(" / ") || "—"}</td>
                  <td className="py-2.5 pr-3 text-slate-600">{(c.detalle || []).length}</td>
                  <td className="py-2.5">
                    <div className="flex justify-end gap-1.5">
                      <button data-testid={`cronograma-editar-btn-${c.id}`} title="Editar" onClick={() => editar(c)}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-indigo-50 text-indigo-600 hover:bg-indigo-100">
                        <Pencil size={13} />
                      </button>
                      <button data-testid={`cronograma-eliminar-btn-${c.id}`} title="Eliminar" onClick={() => eliminar(c)}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-pink-50 text-pink-600 hover:bg-pink-100">
                        <X size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {listado.length === 0 && (
                <tr><td colSpan={6} className="py-6 text-center text-slate-400">No hay cronogramas registrados</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel titulo={form.id ? "Opciones — editando cronograma" : "Opciones"} testId="cronograma-opciones-panel">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Campo label="Nombre" obligatorio error={errores.nombre} testId="cronograma-nombre">
            <Input data-testid="cronograma-nombre-input" value={form.nombre} maxLength={80}
              placeholder="Ej: Plan de mantenimiento 150cc" onChange={(e) => set("nombre", e.target.value)} />
          </Campo>
          <Campo label="Copiar cronograma (opcional)" testId="cronograma-copiar">
            <Combobox testId="cronograma-copiar-combobox" value={copiarId} onChange={copiarDesde}
              placeholder="Clonar desde un cronograma existente"
              options={cronogramas.filter((c) => c.id !== form.id)
                .map((c) => ({ value: c.id, label: c.nombre }))} />
          </Campo>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <Campo label="Indique los períodos de mantenimiento" testId="cronograma-periodos">
            <div className="flex items-center gap-5 h-9">
              {[["kilometraje", "Kilometraje"], ["tiempo", "Tiempo"], ["horas", "Horas"]].map(([k, lbl]) => (
                <label key={k} className="flex items-center gap-1.5 text-sm text-slate-600">
                  <Checkbox data-testid={`cronograma-periodo-${k}`} checked={form.tipos_periodo[k]}
                    onCheckedChange={(v) => set("tipos_periodo", { ...form.tipos_periodo, [k]: !!v })} />
                  {lbl}
                </label>
              ))}
            </div>
            <p className="text-xs text-slate-400">Tiempo y Horas: previstos para una etapa futura</p>
          </Campo>
        </div>
        <div className="mt-6 border-t border-slate-100 pt-4" data-testid="cronograma-servicios-asociados">
          <div className="mb-3">
            <h3 className="text-sm font-semibold text-slate-700">Servicios asociados (opcional)</h3>
            <p className="text-xs text-slate-400">Elija qué servicio(s) "abren" este cronograma al agregarlos a un trabajo. Si no elige ninguno, el cronograma se ofrece como opción genérica para los modelos aplicables.</p>
          </div>
          <div className="max-w-sm">
            <MultiCombobox testId="cronograma-servicios-combobox" values={form.servicio_ids || []}
              onChange={(vals) => set("servicio_ids", vals)}
              placeholder="Seleccione servicios asociados…"
              options={opcionesServiciosAsociados.map((s) => ({ value: s.id, label: s.nombre }))} />
          </div>
          {(form.servicio_ids || []).length > 0 && (
            <div data-testid="cronograma-servicios-chips" className="flex flex-wrap gap-1.5 mt-2">
              {form.servicio_ids.map((id) => (
                <span key={id} data-testid={`cronograma-servicio-chip-${id}`}
                  className="inline-flex items-center gap-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 px-2.5 py-0.5 text-xs">
                  {nombreServicioAsociado(id)}
                  <button type="button" data-testid={`cronograma-servicio-quitar-${id}`} title="Quitar"
                    onClick={() => set("servicio_ids", form.servicio_ids.filter((x) => x !== id))}
                    className="text-indigo-500 hover:text-indigo-800">
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6 border-t border-slate-100 pt-4" data-testid="cronograma-modelos-aplicables">
          <div className="mb-3">
            <h3 className="text-sm font-semibold text-slate-700">Modelos aplicables</h3>
            <p className="text-xs text-slate-400">Marque uno o varios modelos. Cada modelo ya trae su propio rango de años. Puede guardar sin modelos y agregarlos después.</p>
          </div>
          {errores.modelos && <p data-testid="cronograma-modelos-error" className="text-xs text-red-500 mb-2">{errores.modelos}</p>}

          {modelosSel.length > 0 && (
            <div data-testid="cronograma-modelos-chips" className="flex flex-wrap gap-1.5 mb-2">
              {modelosSel.map((id) => (
                <span key={id} data-testid={`cronograma-modelo-chip-${id}`}
                  className="inline-flex items-center gap-1 rounded-full bg-teal-50 text-teal-700 border border-teal-200 px-2.5 py-0.5 text-xs">
                  {nombreModelo(id)}
                  <button type="button" data-testid={`cronograma-modelo-quitar-${id}`} title="Quitar"
                    onClick={() => setModelosSel(modelosSel.filter((x) => x !== id))}
                    className="text-teal-500 hover:text-teal-800">
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}

          <Input data-testid="cronograma-modelos-buscar" value={maBusqueda} className="mb-2 max-w-sm"
            placeholder="Filtrar modelos por nombre…" onChange={(e) => setMaBusqueda(e.target.value)} />
          <ModelosAgrupados modelos={modelosFiltrados} seleccionados={modelosSel} onChange={setModelosSel}
            testPrefix="cronograma-ma" emptyMsg={maBusq ? "Ningún modelo coincide con la búsqueda." : "No hay modelos registrados."} />
        </div>
      </Panel>

      <Panel titulo="Plan de Mantenimiento" testId="cronograma-detalle-panel">
        <div className="flex justify-between items-center mb-3">
          <Button variant="outline" data-testid="cronograma-agregar-fila-btn"
            className="border-teal-500 text-teal-600 hover:bg-teal-50 hover:text-teal-700"
            onClick={() => setFilas([...filas, filaVacia()])}>
            <Plus size={15} className="mr-1" /> Agregar tarea
          </Button>
          <div className="text-right">
            {errores.intervalos && <p data-testid="cronograma-intervalos-error" className="text-xs text-red-500">{errores.intervalos}</p>}
            {errores.filas && <p data-testid="cronograma-filas-error" className="text-xs text-red-500">{errores.filas}</p>}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table data-testid="cronograma-detalle-tabla" className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                <th className="py-2 pr-3 font-semibold min-w-[220px]" rowSpan={2}>Servicio</th>
                <th className="py-2 pr-3 font-semibold text-center border-l border-slate-100" colSpan={N_INTERVALOS}>
                  Frecuencia (x1.000 km)
                </th>
                <th className="py-2 px-2 font-semibold text-center border-l border-slate-100" rowSpan={2}>Chequeo<br />Anual</th>
                <th className="py-2 px-2 font-semibold text-center border-l border-slate-100" rowSpan={2}>Reemplazo<br />Periódico</th>
                <th className="py-2 pl-2 font-semibold text-right border-l border-slate-100" rowSpan={2}>Acción</th>
              </tr>
              <tr className="border-b border-slate-200">
                {Array.from({ length: N_INTERVALOS }).map((_, i) => (
                  <th key={i} className={`py-1.5 px-1 ${i === 0 ? "border-l border-slate-100" : ""}`}>
                    <Input data-testid={`cronograma-intervalo-${i}`} inputMode="decimal"
                      value={intervalos[i]} placeholder="--"
                      onChange={(e) => setIntervalos(intervalos.map((v, j) =>
                        (j === i ? soloDecimal(e.target.value) : v)))}
                      className="h-8 w-16 text-center text-xs font-semibold" />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filas.map((f, i) => (
                <tr key={f.key} data-testid={`cronograma-fila-${i}`} className="border-b border-slate-100">
                  <td className="py-2 pr-3">
                    <Combobox testId={`cronograma-servicio-combobox-${i}`} value={f.servicio_id}
                      onChange={(v) => setFila(f.key, "servicio_id", v)} placeholder="Seleccione Servicio"
                      options={servicios.map((s) => ({ value: s.id, label: s.nombre }))} />
                  </td>
                  {Array.from({ length: N_INTERVALOS }).map((_, col) => (
                    <td key={col} className={`py-2 px-1 text-center ${col === 0 ? "border-l border-slate-50" : ""}`}>
                      {intervalos[col] && parseFloat(intervalos[col]) > 0 ? (
                        <SelectAccion value={f.acciones_km[col]} testId={`cronograma-accion-${i}-${col}`}
                          onChange={(v) => setAccionKm(f.key, col, v)} />
                      ) : (
                        <span className="text-slate-300 text-xs">--</span>
                      )}
                    </td>
                  ))}
                  <td className="py-2 px-2 text-center border-l border-slate-50">
                    <div className="flex justify-center">
                      <SelectAccion value={f.accion_anual} testId={`cronograma-anual-${i}`}
                        onChange={(v) => setFila(f.key, "accion_anual", v)} />
                    </div>
                  </td>
                  <td className="py-2 px-2 border-l border-slate-50">
                    <div className="flex items-center gap-1 justify-center">
                      <Input data-testid={`cronograma-rp-valor-${i}`} inputMode="decimal" value={f.rp_valor}
                        placeholder="--" onChange={(e) => setFila(f.key, "rp_valor", soloDecimal(e.target.value, 3))}
                        className="h-8 w-14 text-center text-xs" />
                      <Select value={f.rp_unidad} onValueChange={(v) => setFila(f.key, "rp_unidad", v)}>
                        <SelectTrigger data-testid={`cronograma-rp-unidad-${i}`} className="h-8 w-24 px-2 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {UNIDADES.map((u) => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </td>
                  <td className="py-2 pl-2 border-l border-slate-50">
                    <div className="flex justify-end items-center gap-1">
                      <button data-testid={`cronograma-subir-${i}`} title="Subir" onClick={() => mover(i, -1)}
                        disabled={i === 0}
                        className="w-7 h-7 flex items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 disabled:opacity-30">
                        <ArrowUp size={14} />
                      </button>
                      <button data-testid={`cronograma-bajar-${i}`} title="Bajar" onClick={() => mover(i, 1)}
                        disabled={i === filas.length - 1}
                        className="w-7 h-7 flex items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 disabled:opacity-30">
                        <ArrowDown size={14} />
                      </button>
                      <button data-testid={`cronograma-eliminar-fila-${i}`} title="Eliminar"
                        onClick={() => setFilas(filas.filter((x) => x.key !== f.key))}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-pink-50 text-pink-600 hover:bg-pink-100">
                        <X size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filas.length === 0 && (
                <tr><td colSpan={N_INTERVALOS + 4} className="py-6 text-center text-slate-400">
                  Agregue al menos una tarea con el botón "+ Agregar tarea"
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-slate-500">
          Acciones: <b>I</b> = Revisar · <b>C</b> = Limpiar · <b>R</b> = Cambiar · <b>A</b> = Ajustar · <b>L</b> = Lubricar · -- = no aplica
        </p>
        <div className="flex justify-end mt-6">
          <Button data-testid="cronograma-guardar-btn" onClick={guardar} disabled={guardando}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-8">
            {guardando ? "Guardando..." : form.id ? "Actualizar" : "Guardar"}
          </Button>
        </div>
      </Panel>
    </div>
  );
}
